/**
 * Which attachments a viewer may show in place, and which have to be offered as
 * a download.
 *
 * `contentType.startsWith('image/')` is the obvious test and the wrong one: the
 * server accepts TIFF scans and AVI clips, and those are `image/tiff` and
 * `video/x-msvideo`. Pointing an <img> or a <video> at one produces a broken
 * tile, and in the case viewer it also auto-opens the gallery onto that broken
 * tile. Only formats a browser actually draws or plays belong here; everything
 * else falls through to the document link, which is what the server's own
 * Content-Disposition already says about them (see AzureStorageService).
 */

const RENDERABLE_IMAGE_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'image/bmp',
]);

const PLAYABLE_VIDEO_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
]);

const normalize = (contentType?: string | null): string =>
  typeof contentType === 'string' ? contentType.split(';')[0].trim().toLowerCase() : '';

/** An image the browser will draw in an <img>. */
export const isRenderableImage = (contentType?: string | null): boolean =>
  RENDERABLE_IMAGE_TYPES.has(normalize(contentType));

/** A clip the browser will play in a <video>. */
export const isPlayableVideo = (contentType?: string | null): boolean =>
  PLAYABLE_VIDEO_TYPES.has(normalize(contentType));

/** Anything a gallery can display — an image to draw or a clip to play. */
export const isVisualMedia = (contentType?: string | null): boolean =>
  isRenderableImage(contentType) || isPlayableVideo(contentType);
